import React, { useEffect, useState } from "react";
import { Box, Collapse, Tooltip, Typography } from "@mui/material";
import CommentIcon from "@mui/icons-material/Comment";
import dayjs from "dayjs";
import { BASEURL } from "../../constant/constant";
import Comments from "./comments/Comments";
import Likes from "./likes/Likes";
import Loader from "../loader/Loader";
import { useNavigate } from "react-router-dom";
import { apiCall } from "../../API/apiCall";

const PreviewBlog = ({ userType, blogId }) => {
  const [showComments, setShowComments] = useState(false);
  const [comments, setComments] = useState([]);
  const [totalCommets, setTotalCommets] = useState(0);
  const [totalLikes, setTotalLikes] = useState(0);
  const [isLoadedComment, setIsLoadedComment] = useState(false);
  const [hasMoreComments, setHasMoreComments] = useState(true);
  const [pageLoaded, setPageLoaded] = useState(null);
  const [page, setPage] = useState(1);

  const [isLoader, setIsLoader] = useState(false);
  const [blog, setBlog] = useState([]);

  const navigate = useNavigate();

  useEffect(() => {
    setIsLoader(true);
    const getSingleBlog = async () => {
      try {
        const response = await apiCall({
          url: `/blogs/${blogId}`,
          method: "GET",
          params: {
            userType: "user",
          },
        });
        setBlog(response?.data?.data);
        setTotalLikes(response?.data?.data?.likes || 0);
        setTotalCommets(response?.data?.data?.comments || 0);
        setIsLoader(false);
      } catch (error) {
        navigate("/blogs");
        setIsLoader(false);
      }
    };
    // Method that will fetch the single blog.
    getSingleBlog();
  }, [blogId]);

  const replaceImagePlaceholders = (content, contentImages) => {
    return content?.replace(/\{image(\d+)\}/g, (_, index) => {
      const imageUrl = contentImages[parseInt(index, 10)];
      return imageUrl
        ? `<img class="blog-content-image" src="${`${BASEURL}/${imageUrl}`}" alt="Blog Image" style="max-width: 100%; height: auto;" />`
        : "";
    });
  };

  const handleCommentIconClick = () => {
    setShowComments((prev) => !prev);
  };

  // Only render the loader or the blog content based on the loading state
  if (isLoader) {
    return <Loader />;
  }

  // If blog is not available, you can return null or a message
  if (!blog) {
    return null; // or <div>No blog data available</div>;
  }

  const backToPreviousPage = () => {
    navigate(-1);
  };

  return (
    <>
      <div className="container py-4">
        <div className="detailprofile-outer">
          <div className="blog-title">
            <span className="backToHome" onClick={backToPreviousPage}>
              <svg
                width="24px"
                height="24px"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                stroke="#cccccc"
              >
                <g id="SVGRepo_bgCarrier" strokeWidth="0"></g>
                <g
                  id="SVGRepo_tracerCarrier"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                ></g>
                <g id="SVGRepo_iconCarrier">
                  {" "}
                  <path
                    d="M4 10L3.64645 10.3536L3.29289 10L3.64645 9.64645L4 10ZM20.5 18C20.5 18.2761 20.2761 18.5 20 18.5C19.7239 18.5 19.5 18.2761 19.5 18L20.5 18ZM8.64645 15.3536L3.64645 10.3536L4.35355 9.64645L9.35355 14.6464L8.64645 15.3536ZM3.64645 9.64645L8.64645 4.64645L9.35355 5.35355L4.35355 10.3536L3.64645 9.64645ZM4 9.5L14 9.5L14 10.5L4 10.5L4 9.5ZM20.5 16L20.5 18L19.5 18L19.5 16L20.5 16ZM14 9.5C17.5898 9.5 20.5 12.4101 20.5 16L19.5 16C19.5 12.9624 17.0376 10.5 14 10.5L14 9.5Z"
                    fill="#222222"
                  ></path>{" "}
                </g>
              </svg>
            </span>
            <h1>{blog.title}</h1>
          </div>
          <div className="details-profile">
            <img src={`${BASEURL}/${blog.author?.profile}`} alt="Author" />
            <div>
              <h6>
                {blog.author?.firstName && blog.author?.lastName
                  ? `${blog.author.firstName} ${blog.author.lastName}`
                  : "Anonymous"}
              </h6>
              <p>{dayjs(blog.createdAt).format("DD MMM YYYY . hh:mm A")}</p>
            </div>
          </div>
          <div className="blog-image">
            <img src={`${BASEURL}/${blog.coverImage}`} alt="cover" />
          </div>
          <div className="blog-content">
            <div
              dangerouslySetInnerHTML={{
                __html: replaceImagePlaceholders(
                  blog.content,
                  blog.contentImages
                ),
              }}
            />
          </div>
          <Box display="flex" alignItems="center" sx={{ padding: 2 }}>
            {blog._id && (
              <Likes
                blogData={{
                  blogId: blog._id,
                  likes: blog.likes,
                }}
                totalLikes={totalLikes}
                setTotalLikes={setTotalLikes}
              />
            )}
            <Box display="flex" alignItems="center">
              <Tooltip
                placement="top"
                title="Click here to add a new comment or explore all existing comments..."
              >
                <CommentIcon
                  onClick={handleCommentIconClick}
                  sx={{ fontSize: 50, color: "#1976d2", cursor: "pointer" }}
                />
              </Tooltip>

              <Typography
                variant="body2"
                component="span"
                sx={{ marginLeft: 0.5, fontWeight: "bold", color: "#333" }}
              >
                {totalCommets} Comments
              </Typography>
            </Box>
          </Box>
          <Collapse in={showComments}>
            {showComments && blog._id && (
              <Comments
                userType={userType}
                blogId={blog?._id}
                comments={comments}
                setComments={setComments}
                isLoadedComment={isLoadedComment}
                setIsLoadedComment={setIsLoadedComment}
                page={page}
                setPage={setPage}
                pageLoaded={pageLoaded}
                setPageLoaded={setPageLoaded}
                hasMoreComments={hasMoreComments}
                setHasMoreComments={setHasMoreComments}
                setTotalCommets={setTotalCommets}
              />
            )}
          </Collapse>
        </div>
      </div>
    </>
  );
};

export default PreviewBlog;
